/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/Components/ui/dialog"

import { FilePlus2 } from "lucide-react";
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/Components/ui/tooltip";
import { columns, User, columnDisplayNames } from "./users_table/columns";
import { DataTableWrapper } from "@/Components/datatable/data-table";
import { useState } from "react";
import UsersForm from "./users_table/UsersForm";
import React from "react";
import { useEffect } from "react";
import { AppContext } from "@/App";
import Confirmation from "@/hooks/confirmation";
import { DeleteIcon } from "../ui/Icons/TooltipIcon";
import { useIsMobile } from "@/hooks/use-mobile";

export type UserColumnVisibility = {
    id: boolean
    forename: boolean
    surname: boolean
    displayName: boolean
    email: boolean
    role: boolean
}

const ManageUsers = () => {
    const isMobile = useIsMobile();
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const appContext = React.useContext(AppContext);
    const [loading, setLoading] = useState<boolean>(true); // add a loading state
    const [users, setUsers] = useState<User[]>([]);

    const [dialogTitle, setDialogTitle] = useState("Create New User");
    const [dialogTitleBarActions, setDialogTitleBarActions] = useState<JSX.Element | null>(null);
    const [dialogContent, setDialogContent] = useState<JSX.Element | null>(null);

    // Fetch users from the database
    useEffect(() => {
        fetch("Users/users")
            .then((response) => response.json())
            .then(users => { setUsers(users); })
            .catch((error) => console.error("Error fetching data:", error))
            .finally(() => setLoading(false)); // set loading to false when the data is fetched
    }, []);

    function showFormDialog(selectedUser?: User) {
        const action = selectedUser ? (
            <div onClick={(e) => {
                e.stopPropagation();
                if (selectedUser?.id) {
                    confirmDeleteUsers([selectedUser?.id]);
                }
            }}
                className="p-2 rounded-md cursor-pointer hover:bg-red-600">
                <DeleteIcon className="w-4 h-4" />
            </div>
        ) : null;

        setDialogTitleBarActions(action);
        setDialogTitle(selectedUser ? "Edit User" : "Create New User");
        setDialogContent(
            <UsersForm
                setUser={saveUser}
                user={selectedUser}
            />
        );
        setDialogOpen(true);
    }

    function confirmDeleteUsers(userIds: string[]) {
        setDialogTitleBarActions(null);
        setDialogTitle(userIds.length > 1 ? "Are you sure you want to delete these users?" : "Are you sure you want to delete this user?");
        setDialogContent(<Confirmation onConfirm={() => { deleteUsers(userIds); setDialogOpen(false); }} onCancel={() => setDialogOpen(false)} confirmColour="bg-chart-red" />);
        setDialogOpen(true);
    }

    function saveUser(user: User) {
        if (user.id) {
            updateUser(user);
            return;
        }
        document.body.style.cursor = 'wait';
        fetch("Users/", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(user),
        })
            .then((response) => {
                if (!response.ok) {
                    appContext.showActionNotification("Error", "Error Adding User", "destructive");
                    throw new Error("Network response was not ok");
                }
                return response.json();
            })
            .then((data) => {
                setUsers((prev) => [data, ...prev]);
                appContext.showActionNotification("Success", `${user.role}: ${data.userName} Added`, "success");
            })
            .catch((error) => {
                appContext.showActionNotification("Error", "Error Adding User", "destructive");
                console.error("Error:", error);
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function loadColumnVisibility(): UserColumnVisibility {
        const localStorageValue = localStorage.getItem(appContext.applicationUser?.id + "_Users_columnVisibility");
        if (localStorageValue) {
            const visibilityValues: { columnId: string, isVisible: boolean }[] = JSON.parse(localStorageValue);
            return ({
                id: visibilityValues.find((v) => v.columnId === "id")?.isVisible ?? false,
                forename: visibilityValues.find((v) => v.columnId === "forename")?.isVisible ?? true,
                surname: visibilityValues.find((v) => v.columnId === "surname")?.isVisible ?? true,
                displayName: visibilityValues.find((v) => v.columnId === "displayName")?.isVisible ?? true,
                email: visibilityValues.find((v) => v.columnId === "email")?.isVisible ?? true,
                role: visibilityValues.find((v) => v.columnId === "role")?.isVisible ?? true,
            })
        }
        return {
            id: false,
            forename: true,
            surname: true,
            displayName: true,
            email: true,
            role: true,
        }
    }

    function deleteUsers(userIds: string[]) {
        document.body.style.cursor = 'wait';
        // check if deleting themselves
        fetch("Users/", {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(userIds),
        })
            .then(() => {
                setUsers(users.filter((user) => !userIds.includes(user.id as string)));
                appContext.showActionNotification("Success", userIds.length > 1 ? "Users Deleted" : "User Deleted", "success");
            })
            .catch((error) => {
                appContext.showActionNotification("Error", error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function editUser(id: string) {
        showFormDialog(users.find((u) => u.id === id) || undefined);
    }

    function updateUser(user: User) {
        // check if user is changing their own role
        if (user.id === appContext.applicationUser?.id && user.role !== appContext.applicationUser?.role) {
            appContext.showActionNotification("Action Disallowed", "Cannot change your own role", "default");
            return;
        }

        document.body.style.cursor = 'wait';
        fetch(`Users/${user.id}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(user),
        })
            .then(() => {
                setUsers(users.map((u) => (u.id === user.id ? user : u)));
                appContext.showActionNotification("Success", `${user.role}: ${user.userName} Updated`, "success");
            })
            .catch((error) => {
                appContext.showActionNotification("Error", error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function setRole(id: string, role: "User" | "Admin") {
        // check if user is changing their own role
        if (id === appContext.applicationUser?.id) {
            appContext.showActionNotification("Action Disallowed", "Cannot change your own role", "default");
            return;
        }

        const user = users.find((u) => u.id === id);
        if (user) {
            document.body.style.cursor = 'wait';
            fetch(`Users/${id}/role/${role}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(role),
            })
                .then(() => {
                    setUsers(users.map((u) => (u.id === id ? { ...u, role } : u)));
                    appContext.showActionNotification("Success", `${role}: ${user.userName} Updated`, "success");
                })
                .catch((error) => {
                    appContext.showActionNotification("Error", error.message, "destructive");
                })
                .finally(() => {
                    document.body.style.cursor = 'default';
                });
        }
    }

    function confirmChangeRole(id: string, role: "User" | "Admin") {
        setDialogTitle("Are you sure you want to change this user's role?");
        setDialogContent(<Confirmation onConfirm={() => { setRole(id, role); setDialogOpen(false); }} onCancel={() => setDialogOpen(false)} confirmColour="bg-chart-red" />);
        setDialogOpen(true);
    }

    function confirmGroupChangeRole(userRoles: { userId: string, newRole: "User" | "Admin" }[]) {
        setDialogTitle("Are you sure you want to change the selected users' roles?");
        setDialogContent(<Confirmation onConfirm={() => { setRolesByGroup(userRoles); setDialogOpen(false); }} onCancel={() => setDialogOpen(false)} confirmColour="bg-chart-red" />);
        setDialogOpen(true);
    }

    function confirmResetPassword(userId: string) {
        setDialogTitle("Are you sure you want to reset this user's password?");
        setDialogContent(<Confirmation onConfirm={() => { resetPassword(userId); setDialogOpen(false); }} onCancel={() => setDialogOpen(false)} confirmColour="bg-chart-red" />);
        setDialogOpen(true);
    }

    function resetPassword(userId: string) {
        document.body.style.cursor = 'wait';
        fetch(`Users/admin-reset-password`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({ userId: userId }),
        })
            .then((response) => {
                if (!response.ok) {
                    appContext.showActionNotification("Error", "Error sending password reset email", "destructive");
                    return;
                }
                return response.json();
            })
            .then((data) => {
                if (data) {
                    appContext.showActionNotification("Success", data.message || "Password reset email sent", "success");
                }
            })
            .catch((error) => {
                appContext.showActionNotification("Error", error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    function setRolesByGroup(userRoles: { userId: string, newRole: "User" | "Admin" }[]) {
        // check if user is changing their own role
        if (userRoles.some((selected) => selected.userId === appContext.applicationUser?.id)) {
            appContext.showActionNotification("Action Disallowed", "Cannot change your own role", "default");
            return;
        }
        document.body.style.cursor = 'wait';
        fetch("Users/roles", {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(userRoles),
        })
            .then(() => {
                setUsers(users.map((u) => {
                    const selectedRole = userRoles.find((selected) => selected.userId === u.id)?.newRole;
                    return selectedRole ? { ...u, role: selectedRole } : u;
                }));
                appContext.showActionNotification("Success", "Users Updated", "success");
            }
            )
            .catch((error) => {
                appContext.showActionNotification("Error", error.message, "destructive");
            })
            .finally(() => {
                document.body.style.cursor = 'default';
            });
    }

    React.useEffect(() => {
        const down = (e: KeyboardEvent) => {
            if (e.key === "r" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault()
                showFormDialog();
            }
        }
        document.addEventListener("keydown", down)
        return () => document.removeEventListener("keydown", down)
    }, []);

    const groupActions = [
        {
            name: "Delete",
            action: (selectedRows: any[]) => {
                if (selectedRows.some((selected) => selected.id === appContext.applicationUser?.id)) {
                    appContext.showActionNotification("Action Disallowed", "Cannot delete yourself", "default");
                    return;
                }
                confirmDeleteUsers(selectedRows.map((row) => row.id));
            },
        },
        {
            name: "Set Role to User",
            action: (selectedRows: any[]) => {
                const userRoles = selectedRows.map((row) => ({ "userId": String(row.id), "newRole": "User" as "User" | "Admin" }));
                confirmGroupChangeRole(userRoles);
                // setRolesByGroup(userRoles);
            },
        },
        {
            name: "Set Role to Admin",
            action: (selectedRows: any[]) => {
                const userRoles = selectedRows.map((row) => ({ "userId": String(row.id), "newRole": "Admin" as "User" | "Admin" }));
                confirmGroupChangeRole(userRoles);
                // setRolesByGroup(userRoles);
            },
        }
    ];

    return (
        <div className={`${isMobile ? "" : "m-2 p-2"}`}>
            <div className="flex flex-row justify-between">
                {isMobile ? <button className="w-full m-2 bg-table-header text-table-header-foreground p-2" onClick={() => showFormDialog()}>Add New User</button> : <>
                <h1 className="text-2xl my-auto p-2">Users</h1>
                <div className="flex justify-end">
                    <TooltipProvider delayDuration={1}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <div
                                    onClick={() => showFormDialog()}
                                    className="rounded-full bg-table-header text-table-header-foreground w-min p-2 hover:bg-blue-400 hover:cursor-pointer m-2"
                                >
                                    <FilePlus2 />
                                </div>
                            </TooltipTrigger>
                            <TooltipContent className="flex flex-col bg-background">
                                <div className="flex flex-row text-muted-foreground place-content-between">
                                    <span className="p-2">Create New User</span>
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>

                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle><div className="flex"><div className="my-auto mr-2">{dialogTitle}</div>{dialogTitleBarActions}</div></DialogTitle>
                            </DialogHeader>
                            {dialogContent}
                        </DialogContent>
                    </Dialog>
                </div></>}
                </div>
            <DataTableWrapper
                columns={columns(
                    (id: string, role: "User" | "Admin") => confirmChangeRole(id, role),
                    (id: string) => confirmDeleteUsers([id]),
                    (id: string) => editUser(id),
                    (userId: string) => confirmResetPassword(userId),
                )}
                table_title="Users"
                columnDisplayNames={columnDisplayNames}
                data={users}
                setData={setUsers}
                loading={loading}
                viewOnly={appContext.authLevel < 4} // viewer can see but not edit
                groupActions={groupActions}
                columnVisibility={loadColumnVisibility()}
            />
        </div>
    );
}

export default ManageUsers